import { Box, Typography } from '@mui/material'
import { ALL_NUTS } from '../common/common'
import { COLOR_PRIMARY, DARK_700, DARK_800 } from '../theme'
import { UK_REGION_PATHS } from '../common/UKRegionPaths'

const VIEW_W = 520
const VIEW_H = 610

const GB_NUTS = ALL_NUTS.filter(n => n !== 'ireland')
const IE_NUTS = ['ireland']

function fmtCount(n: number): string {
    return n.toLocaleString('en-GB')
}

// Badge dimensions in SVG user units
const BADGE_H = 24
const BADGE_RX = 12
const FONT_SIZE = 13
// Approximate pixel width per character at this font size (SVG units)
const CHAR_W = 7.8
const BADGE_PAD = 14

function badgeW(label: string): number {
    return Math.max(40, label.length * CHAR_W + BADGE_PAD * 2)
}

const countoffset: Record<string, Array<number>> = {
    'north-east-england': [0, 0],
    'north-west-england': [0, 0],
    'yorkshire-and-the-humber-england': [0, 10],
    'east-midlands-england': [0, -10],
    'west-midlands-england': [0, 10],
    'east-of-england': [0, 0],
    'greater-london-england': [40, 0],
    'south-east-england': [-10,25],
    'south-west-england': [0, 0],
    'wales': [0, 0],
    'scotland': [-20, 80],
    'northern-ireland': [0, 0],
    'ireland': [0, 0],
}

// Compute centroids once at module load using an off-screen SVG
function computeCentroids(): Record<string, { x: number; y: number }> {
    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg')
    svg.setAttribute('width', '1000')
    svg.setAttribute('height', '1000')
    svg.style.cssText = 'position:absolute;visibility:hidden;top:-9999px;left:-9999px'
    document.body.appendChild(svg)

    const result: Record<string, { x: number; y: number }> = {}
    Object.entries(UK_REGION_PATHS).forEach(([id, d]) => {
        const path = document.createElementNS('http://www.w3.org/2000/svg', 'path')
        path.setAttribute('d', d)
        svg.appendChild(path)
        const bb = path.getBBox()
        const offset = countoffset[id] ?? [0, 0]
        result[id] = { x: bb.x + bb.width / 2 + offset[0], y: bb.y + bb.height / 2 + offset[1] }
        svg.removeChild(path)
    })

    document.body.removeChild(svg)
    return result
}

const CENTROIDS = computeCentroids()

export function UKRegionPicker({ selected, onChange, counts = {}, globalCounts = {} }: {
    selected: string[]
    onChange: (nuts: string[]) => void
    counts?: Record<string, number>
    globalCounts?: Record<string, number>
}) {
    const allSelected = selected.length === ALL_NUTS.length
    const allGBSelected = GB_NUTS.every(n => selected.includes(n))
    const allIESelected = IE_NUTS.every(n => selected.includes(n))

    const toggle = (id: string) =>
        onChange(selected.includes(id) ? selected.filter(r => r !== id) : [...selected, id])

    const toggleGB = () => allGBSelected
        ? onChange(selected.filter(n => !GB_NUTS.includes(n)))
        : onChange([...new Set([...selected, ...GB_NUTS])])
    const toggleIE = () => allIESelected
        ? onChange(selected.filter(n => !IE_NUTS.includes(n)))
        : onChange([...new Set([...selected, ...IE_NUTS])])
    const clearAll = () => onChange([])
    const selectAll = () => onChange([...ALL_NUTS])

    return (
        <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1, color: DARK_800, }}>
                <Typography variant="body2" sx={{
                    textTransform: 'uppercase',
                    fontWeight: 500,
                    color: 'white',
                }}
                >Filter by Region</Typography>
                <Typography
                    variant="body2"
                    onClick={allSelected ? clearAll : selectAll}
                    sx={{ color: COLOR_PRIMARY, cursor: 'pointer', fontWeight: 600, fontSize: '12px' }}
                >
                    {allSelected ? 'Clear all' : 'Select all'}
                </Typography>
            </Box>

            <Box sx={{ width: '100%', borderRadius: 2, p: 1, overflow: 'hidden', border: '1px solid rgba(0,0,0,0.08)', backgroundColor: 'rgba(164,190,220,0.2)' }}>
                <svg
                    viewBox={`0 0 ${VIEW_W} ${VIEW_H}`}
                    style={{ display: 'block', width: '100%', height: 'auto' }}
                >
                    {/* Region paths */}
                    {Object.entries(UK_REGION_PATHS).map(([id, d]) => {
                        const on = selected.includes(id)
                        const isNut = ALL_NUTS.includes(id)
                        return (
                            <path
                                key={id}
                                d={d}
                                onClick={isNut ? () => toggle(id) : undefined}
                                style={{ cursor: isNut ? 'pointer' : 'default' }}
                                fill={on ? 'rgba(244,181,51,0.85)' : 'rgba(164,190,220,0.5)'}
                                stroke="#1a2e40"
                                strokeWidth={1}
                                strokeLinejoin="round"
                            />
                        )
                    })}

                    {/* Count badges — white pill with count, centred on each region */}
                    {ALL_NUTS.map(id => {
                        const count = counts[id]
                        const c = CENTROIDS[id]
                        if (!count || !c) return null
                        const label = fmtCount(count)
                        const bw = badgeW(label)
                        return (
                            <g key={`badge-${id}`} transform={`translate(${c.x},${c.y})`} pointerEvents="none">
                                <rect
                                    x={-bw / 2}
                                    y={-BADGE_H / 2}
                                    width={bw}
                                    height={BADGE_H}
                                    rx={BADGE_RX}
                                    fill="white"
                                    stroke={`${COLOR_PRIMARY}99`}
                                    strokeWidth={1.5}
                                />
                                <text
                                    textAnchor="middle"
                                    dominantBaseline="central"
                                    fontSize={FONT_SIZE}
                                    fontWeight={700}
                                    fontFamily="Inter, sans-serif"
                                    fill="#1a2035"
                                >
                                    {label}
                                </text>
                            </g>
                        )
                    })}
                </svg>
            </Box>

            {/* Footer row */}
            <Box sx={{ display: 'flex', gap: 1.5, mt: 1.5, alignItems: 'center', flexWrap: 'wrap' }}>
                {/* GB flag + UK total */}
                <Box
                    onClick={toggleGB}
                    title={allGBSelected ? 'Deselect Great Britain' : 'Select Great Britain'}
                    sx={{ display: 'flex', alignItems: 'center', gap: 0.75, cursor: 'pointer', opacity: allGBSelected ? 1 : 0.7, transition: 'opacity 0.15s', '&:hover': { opacity: 1 } }}
                >
                    <Box sx={{ borderRadius: '3px', overflow: 'hidden', lineHeight: 0, flexShrink: 0 }}>
                        <img src="/flag-gb.svg" alt="UK flag" width={28} height={18} style={{ display: 'block' }} />
                    </Box>
                    {globalCounts.UK != null && (
                        <Typography sx={{ fontSize: '11px', fontWeight: 700, color: 'white' }}>
                            {globalCounts.UK.toLocaleString('en-GB')}
                        </Typography>
                    )}
                </Box>

                {/* IE flag + IE total */}
                <Box
                    onClick={toggleIE}
                    title={allIESelected ? 'Deselect Ireland' : 'Select Ireland'}
                    sx={{ display: 'flex', alignItems: 'center', gap: 0.75, cursor: 'pointer', opacity: allIESelected ? 1 : 0.7, transition: 'opacity 0.15s', '&:hover': { opacity: 1 } }}
                >
                    <Box sx={{ borderRadius: '3px', overflow: 'hidden', lineHeight: 0, flexShrink: 0 }}>
                        <img src="/flag-ie.svg" alt="Ireland flag" width={28} height={18} style={{ display: 'block' }} />
                    </Box>
                    {counts['ireland'] != null && (
                        <Typography sx={{ fontSize: '11px', fontWeight: 700, color: 'white' }}>
                            {counts['ireland'].toLocaleString('en-GB')}
                        </Typography>
                    )}
                </Box>

                <Typography sx={{ ml: 'auto', fontSize: '11px', color: 'white' }}>
                    {selected.length === 0
                        ? 'No regions'
                        : selected.length === ALL_NUTS.length
                            ? 'All regions'
                            : `${selected.length} region${selected.length !== 1 ? 's' : ''}`}
                </Typography>
            </Box>
        </Box >
    )
}
